/**
 * src/hooks/useFriends.ts
 *
 * Friends graph: accepted friends, incoming requests (drives the sidebar
 * badge), outgoing requests, and the mutating actions.
 *
 * Backed by `public.friends`, created in migration 20260918000000. Before
 * that migration the table did not exist at all — App.tsx's original
 * handleFriendRequest() upserted into it and destructured only `data`, so
 * every request 404'd silently and no friendship was ever stored.
 *
 * Row model (one row per direction):
 *   request  → (requester, target, 'pending')          — one row
 *   accepted → both directions exist with 'accepted'    — two rows
 * so "is X my friend" stays a single-row lookup and the flat `friends`
 * string[] the rest of the app already consumes keeps working.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface FriendProfile {
  id: string;
  name: string;
  avatar: string;
  presence?: string;
  bio?: string;
}

export interface FriendsState {
  friends: FriendProfile[];
  incoming: FriendProfile[];
  outgoing: FriendProfile[];
  loading: boolean;
}

/** Shape a `profiles` row into the display record the panel renders. */
function toProfile(p: any): FriendProfile {
  return {
    id: p.id,
    name: p.display_name || 'Unknown User',
    avatar: p.photo_url || '',
    presence: p.presence || 'Offline',
    bio: p.bio || '',
  };
}

export function useFriends(currentUserId?: string) {
  const [state, setState] = useState<FriendsState>({
    friends: [],
    incoming: [],
    outgoing: [],
    loading: true,
  });

  const refresh = useCallback(async () => {
    if (!currentUserId) {
      setState({ friends: [], incoming: [], outgoing: [], loading: false });
      return;
    }

    // RLS already restricts `friends` to rows where the caller is a party,
    // but filtering explicitly keeps the query planner on the indexes and
    // makes the intent readable.
    const [{ data: mine, error: mineErr }, { data: toMe, error: toMeErr }] = await Promise.all([
      supabase.from('friends').select('friend_id, status').eq('user_id', currentUserId),
      supabase.from('friends').select('user_id, status').eq('friend_id', currentUserId),
    ]);

    if (mineErr || toMeErr) {
      // Most likely PGRST205 — the migration has not been applied yet.
      console.error('useFriends: query failed', mineErr ?? toMeErr);
      setState({ friends: [], incoming: [], outgoing: [], loading: false });
      return;
    }

    const acceptedIds = (mine ?? []).filter(r => r.status === 'accepted').map(r => r.friend_id);
    const outgoingIds = (mine ?? []).filter(r => r.status === 'pending').map(r => r.friend_id);
    const incomingIds = (toMe ?? []).filter(r => r.status === 'pending').map(r => r.user_id);

    const allIds = Array.from(new Set([...acceptedIds, ...outgoingIds, ...incomingIds]));
    if (allIds.length === 0) {
      setState({ friends: [], incoming: [], outgoing: [], loading: false });
      return;
    }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, photo_url, presence, bio')
      .in('id', allIds);

    const byId = new Map<string, FriendProfile>((profiles ?? []).map(p => [p.id, toProfile(p)]));
    const pick = (ids: string[]) => ids.map(id => byId.get(id)).filter(Boolean) as FriendProfile[];

    setState({
      friends: pick(acceptedIds),
      incoming: pick(incomingIds),
      outgoing: pick(outgoingIds),
      loading: false,
    });
  }, [currentUserId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Live-update both parties: the sender sees "pending" clear on accept, the
  // receiver's badge lights up the moment a request lands.
  useEffect(() => {
    if (!currentUserId) return;
    const channel = supabase
      .channel('friends_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'friends' }, (payload) => {
        const row: any = payload.new ?? payload.old;
        if (row && row.user_id !== currentUserId && row.friend_id !== currentUserId) return;
        void refresh();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId, refresh]);

  /**
   * Look a user up by display name / callsign and send them a request.
   * Returns a human-readable outcome for the panel to surface inline.
   */
  const sendRequestByUsername = useCallback(
    async (username: string): Promise<{ ok: boolean; message: string }> => {
      if (!currentUserId) return { ok: false, message: 'Not signed in.' };
      const q = username.trim();
      if (!q) return { ok: false, message: 'Enter a username.' };

      // Escape PostgREST wildcards to prevent unintended matches
      const escapedQ = q.replace(/[%_]/g, '\\$&');

      const { data: matches, error } = await supabase
        .from('profiles')
        .select('id, display_name')
        .ilike('display_name', `%${escapedQ}%`)
        .limit(2);

      if (error) return { ok: false, message: 'Lookup failed. Try again.' };
      if (!matches || matches.length === 0) return { ok: false, message: `No user named "${q}".` };
      if (matches.length > 1) return { ok: false, message: `"${q}" is ambiguous — more than one match.` };

      const target = matches[0];
      if (target.id === currentUserId) return { ok: false, message: "You can't friend yourself." };

      const { error: insErr } = await supabase
        .from('friends')
        .upsert({ user_id: currentUserId, friend_id: target.id, status: 'pending' });

      if (insErr) {
        return { ok: false, message: insErr.message || 'Could not send request.' };
      }

      await refresh();
      return { ok: true, message: `Request sent to ${target.display_name}.` };
    },
    [currentUserId, refresh]
  );

  const acceptRequest = useCallback(
    async (requesterId: string) => {
      const { error } = await supabase.rpc('accept_friend_request', { p_requester: requesterId });
      if (error) console.error('accept_friend_request failed:', error.code, error.message);
      await refresh();
    },
    [refresh]
  );

  /** Decline an incoming request, cancel an outgoing one, or unfriend. */
  const removeFriend = useCallback(
    async (otherId: string) => {
      const { error } = await supabase.rpc('remove_friend', { p_other: otherId });
      if (error) console.error('remove_friend failed:', error.code, error.message);
      await refresh();
    },
    [refresh]
  );

  return {
    ...state,
    refresh,
    sendRequestByUsername,
    acceptRequest,
    removeFriend,
  };
}
